import { BTN, type Fx, type InputTrace, type RealtimeSim, roundPointer, TICK_MS, traceRecorder } from '@wyn/game-core';
import { useEffect, useRef, useState } from 'react';

/**
 * Runs a real-time minigame on the same fixed step the server replays: input is sampled once per
 * tick (keys, held on-screen buttons, and the pointer for games aimed with it), recorded, and fed
 * to the simulation. Rendering is free to interpolate; only the recorded trace counts. When the
 * attempt ends, `onDone` gets the trace.
 */
export function useRealtime<S extends RealtimeSim>(opts: {
  create: () => S;
  /** Called every animation frame after stepping. `dt` is real seconds since the last frame. */
  onFrame?: (sim: S, dt: number, now: number) => void;
  onDone: (sim: S, trace: InputTrace) => void;
  /** Keyboard code → mask bit. Defaults to Space for the main button. */
  keys?: Record<string, number>;
  /** The pointer in the game's canvas units, read once per tick, for engines that aim with it. */
  pointer?: () => { x: number; y: number };
}) {
  const simRef = useRef<S | null>(null);
  const held = useRef({ keys: 0, pointer: 0 });
  const [, setFrame] = useState(0);
  const [fx, setFx] = useState<Fx | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const sim = optsRef.current.create();
    simRef.current = sim;
    const rec = traceRecorder();
    let acc = 0;
    let last = performance.now();
    let seenFx = 0;
    let done = false;
    let raf = 0;

    const tick = (now: number) => {
      const elapsed = Math.min(250, now - last);
      last = now;
      acc += elapsed;
      // Never run ahead of real time: the server checks that a run took at least as long as it claims.
      let steps = 0;
      while (!done && acc >= TICK_MS && steps < 12) {
        const mask = held.current.keys | held.current.pointer;
        const p = optsRef.current.pointer?.();
        const x = p ? roundPointer(p.x) : 0, y = p ? roundPointer(p.y) : 0;
        rec.sample(sim.tick, mask, p ? x : undefined, p ? y : undefined);
        sim.step(mask, x, y);
        acc -= TICK_MS;
        steps++;
        if (sim.result) {
          done = true;
          optsRef.current.onDone(sim, rec.trace(sim.tick));
        }
      }
      if (sim.fx.length > seenFx) {
        setFx(sim.fx[sim.fx.length - 1]!);
        seenFx = sim.fx.length;
      }
      optsRef.current.onFrame?.(sim, elapsed / 1000, now);
      setFrame((f) => f + 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const key = (down: boolean) => (e: KeyboardEvent) => {
      const bit = (optsRef.current.keys ?? { Space: BTN.main })[e.code];
      if (!bit) return;
      e.preventDefault();
      if (e.repeat) return;
      held.current.keys = down ? held.current.keys | bit : held.current.keys & ~bit;
    };
    const kd = key(true), ku = key(false);
    const blur = () => (held.current = { keys: 0, pointer: 0 });
    addEventListener('keydown', kd);
    addEventListener('keyup', ku);
    addEventListener('blur', blur);
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('keydown', kd);
      removeEventListener('keyup', ku);
      removeEventListener('blur', blur);
    };
  }, []);

  /** Props for anything the player holds down (the big button, the canvas), setting `bit` while held. */
  const holdProps = (bit: number = BTN.main) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      held.current.pointer |= bit;
    },
    onPointerUp: () => void (held.current.pointer &= ~bit),
    onPointerCancel: () => void (held.current.pointer &= ~bit),
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });
  const isHeld = (bit: number = BTN.main) => ((held.current.keys | held.current.pointer) & bit) !== 0;
  return { sim: simRef.current, fx, holdProps, isHeld };
}

/** Converts a pointer event to canvas units for a canvas drawn at `width` units wide. */
export function canvasPoint(canvas: HTMLCanvasElement, e: { clientX: number; clientY: number }, width: number) {
  const r = canvas.getBoundingClientRect();
  const k = width / r.width;
  return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k };
}
